import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';

import { ClassNameMap, makeStyles } from '@mui/styles';

import { ProgramTaskStatus } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import { MemberDashboardProgramTask } from '@vestahealthcare/common/models';
import { DATE_FORMAT_SHORT } from '@vestahealthcare/common/utils/constants';

import {
  BackgroundColors,
  CollapseIcon,
  Colors,
  IconButton,
  StatusIndicator,
  Table,
  TableItem,
  UNASSIGNED,
} from 'styleguide-v2';

export interface TaskTableModel extends MemberDashboardProgramTask, TableItem {}

type Props = {
  className?: string;
  child?: boolean;
  data?: MemberDashboardProgramTask[];
};

const OPEN_TASK_COLUMNS = (
  styles: ClassNameMap<'firstParragraph' | 'status'>,
  onToggleDetail: (task: TaskTableModel, index: number) => void,
) => [
  {
    headerName: 'Task',
    component: ({ taskName, taskDate }: MemberDashboardProgramTask) => (
      <>
        <p className={classNames(styles.firstParragraph, 'bold no-margin')}>
          {taskName}
        </p>
        <p className="gray no-margin semi-bold size-xs">
          {taskDate.format(DATE_FORMAT_SHORT)}
        </p>
      </>
    ),
  },
  {
    headerName: 'Status',
    component: ({ assignee, status }: MemberDashboardProgramTask) => (
      <StatusIndicator
        className={styles.status}
        color={ProgramTaskStatus.getColor(status)}
        status={status.toString()}
        subtitle={assignee ?? UNASSIGNED?.fullName}
      />
    ),
    width: 175,
  },
  {
    headerName: ' ',
    component: (task: TaskTableModel, index: number) => {
      return task.children?.length ? (
        <IconButton
          noMargin
          onClick={(evt) => {
            evt.stopPropagation();
            onToggleDetail(task, index);
          }}
          size="small"
        >
          <CollapseIcon
            open={!!task.open}
            data-cy={`member-dashboard-ccm-task-item-${index}-collapse`}
          />
        </IconButton>
      ) : (
        <></>
      );
    },
    width: 56,
  },
];

const useStyles = makeStyles({
  table: {
    '&&': {
      marginTop: 1,
      '&& tbody': {
        '& > tr.detail > td': {
          background: Colors.white,
          padding: '0',
        },
      },
    },
  },
  firstParragraph: {
    '&&': {
      margin: '4px 0 -2px',
    },
  },
  noPaddingTable: {
    paddingTop: 0,
    marginTop: 0,

    '&& thead': {
      display: 'none',
    },
    '&& tbody': {
      borderRadius: 0,
      boxShadow: 'none',
      borderLeft: `1px solid ${BackgroundColors.lightGray}`,
      borderRight: `1px solid ${BackgroundColors.lightGray}`,

      '& tr td': {
        backgroundColor: `${BackgroundColors.lighterGray}!important`,
      },
    },
    '&& tbody tbody': {
      borderLeft: 0,
      borderRight: 0,
    },
  },
  status: {
    '& > :first-child': {
      margin: '4px 0 0',
    },
    '& > :last-child': {
      marginTop: '-3px',
    },
  },
});

export const RecentProgramTasksTable = (props: Props) => {
  const styles = useStyles();
  const [data, setData] = useState<TaskTableModel[]>([]);

  const onToggleDetail = (item: TaskTableModel) => {
    item.open = !item.open;
    if (item.open) {
      item.detail = (
        <RecentProgramTasksTable child data={item.children || []} />
      );
    } else {
      delete item.detail;
    }
    setData([...data]);
  };

  const columns = useMemo(() => OPEN_TASK_COLUMNS(styles, onToggleDetail), [
    onToggleDetail,
  ]);

  useEffect(() => setData(props.data || []), [props.data]);

  return (
    <Table
      className={classNames(
        styles.table,
        props.child && styles.noPaddingTable,
        props.className,
      )}
      config={{
        columns,
        compact: true,
        data,
        detail: !props.child,
        paginationOptions: [-1],
      }}
      fontSize="small"
      empty={translate('memberProfile.dashboard.noRecentCCMTasks')}
      whiteBackground
    />
  );
};
